import { action, computed, flow, makeAutoObservable, observable } from 'mobx'

import { DotLabelType } from 'Shared/molecules/DotLabels'
import { openPdfDocumentFromBlob } from 'Shared/utils/file'
import { requestWrapper } from 'Shared/utils/network'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces/core'
import {
	deleteSubstring,
	emptyCustomLabelValue,
	entityToLabelWithDashValue
} from 'Core/utils/dataFormat'
import { getDefaultStoreArrayValue, getDefaultStoreValue } from 'Core/utils/storeHelpers'

import { Wastestream } from 'Portals/Customer/entities'
import { WastestreamRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

import { WastestreamTable } from './WastestreamTable'

export class WastestreamStore {
	table: WastestreamTable
	@observable wastestream = getDefaultStoreValue<Wastestream.Detailed>()
	@observable public serviceSites = getDefaultStoreArrayValue<Entity>()

	constructor(public rootStore: RootStore) {
		this.table = new WastestreamTable(rootStore)
		makeAutoObservable(this, {}, { autoBind: true })
	}

	@flow
	*retrieveWastestream(id: Key): CustomGenerator<Wastestream.Detailed> {
		yield requestWrapper({
			field: this.wastestream,
			repository: WastestreamRepo.retrieveWastestream,
			repositoryParams: id
		})
	}

	@computed
	get wastestreamImages() {
		if (!this.wastestream.value) {
			return []
		}
		const { dotLabel1FileName, dotLabel2FileName, dotLabel3FileName } = this.wastestream.value
		return [dotLabel1FileName, dotLabel2FileName, dotLabel3FileName]
			.filter(Boolean)
			.map((name) => deleteSubstring(name as string, '.jpg')) as DotLabelType[]
	}

	@flow
	*downloadProfileFile(): CustomGenerator<Blob> {
		if (!this.wastestream.value) {
			return
		}
		const { id, profileFilename } = this.wastestream.value

		try {
			const file = yield WastestreamRepo.downloadProfileFile(id)
			openPdfDocumentFromBlob(file, profileFilename)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification()
		}
	}

	@action
	resetWastestream = () => {
		this.wastestream = getDefaultStoreValue()
	};

	@flow.bound
	*retrieveServiceSites() {
		yield requestWrapper({
			field: this.serviceSites,
			repository: WastestreamRepo.retrieveWasteStreamServiceSites,
			repositoryParams: null
		})
	}

	@computed
	get serviceSitesOptions(): LabelValue[] {
		return [
			emptyCustomLabelValue('All'),
			...this.serviceSites.value.map(entityToLabelWithDashValue)
		]
	}
}
